// Vite bundles every Playbook markdown file at build time.
const rawFiles = import.meta.glob("/docs/playbook/**/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

// Tiny browser-safe frontmatter parser. We intentionally avoid `gray-matter`
// here: it pulls in Node's `Buffer` at module init, which throws
// `ReferenceError: Buffer is not defined` in the browser bundle and freezes
// the app on the static "Loading…" shell before React can mount.
// Frontmatter in this project is plain `key: value` / `key: [a, b]` lines —
// no nested YAML — so a line-based parser is sufficient.
function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  if (!raw.startsWith("---")) return { data: {}, content: raw };
  const end = raw.indexOf("\n---", 3);
  if (end === -1) return { data: {}, content: raw };
  const header = raw.slice(3, end).replace(/^\r?\n/, "");
  const rest = raw.slice(end + 4).replace(/^\r?\n/, "");
  const data: Record<string, unknown> = {};
  for (const line of header.split(/\r?\n/)) {
    const m = line.match(/^([A-Za-z0-9_-]+)\s*:\s*(.*)$/);
    if (!m) continue;
    const key = m[1];
    let value: string = m[2].trim();
    // Strip surrounding quotes
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    if (value.startsWith("[") && value.endsWith("]")) {
      const inner = value.slice(1, -1).trim();
      const items = inner.length
        ? inner.split(",").map((s) => s.trim().replace(/^['"]|['"]$/g, ""))
        : [];
      const nums = items.map((s) => Number(s));
      data[key] = nums.every((n) => !Number.isNaN(n)) && items.length
        ? nums
        : items;
      continue;
    }
    if (value === "true" || value === "false") {
      data[key] = value === "true";
      continue;
    }
    if (value !== "" && !Number.isNaN(Number(value))) {
      data[key] = Number(value);
      continue;
    }
    data[key] = value;
  }
  return { data, content: rest };
}

export type PlaybookPage = {
  /** URL slug, e.g. "capabilities/receiving" or "index" or "GLOSSARY" */
  slug: string;
  /** Section the page belongs to. */
  section: "root" | "principles" | "capabilities" | "reference" | "glossary";
  title: string;
  body: string;
  frontmatter: {
    capability?: string;
    status?: "prod-ready" | "demo-ready" | "code-complete" | "planned";
    last_verified?: string;
    last_verified_sprint?: number;
    sprint_refs?: number[];
    related?: string[];
    [k: string]: unknown;
  };
  headings: { depth: number; text: string; id: string }[];
};

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function deriveTitle(body: string, slug: string): string {
  const m = body.match(/^#\s+(.+)$/m);
  return m ? m[1].trim() : slug;
}

function extractHeadings(body: string): { depth: number; text: string; id: string }[] {
  const out: { depth: number; text: string; id: string }[] = [];
  const lines = body.split("\n");
  let inFence = false;
  for (const line of lines) {
    if (line.startsWith("```")) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = line.match(/^(#{2,4})\s+(.+?)\s*$/);
    if (m) out.push({ depth: m[1].length, text: m[2], id: slugifyHeading(m[2]) });
  }
  return out;
}

function sectionOf(slug: string): PlaybookPage["section"] {
  if (slug.startsWith("principles/")) return "principles";
  if (slug.startsWith("capabilities/")) return "capabilities";
  if (slug.startsWith("reference/")) return "reference";
  if (slug === "GLOSSARY") return "glossary";
  return "root";
}

function buildPages(): PlaybookPage[] {
  const pages: PlaybookPage[] = [];
  for (const [path, raw] of Object.entries(rawFiles)) {
    // path looks like "/docs/playbook/capabilities/receiving.md"
    const rel = path.replace(/^\/docs\/playbook\//, "").replace(/\.md$/, "");
    // Skip CONTRIBUTING — not part of the reader surface.
    if (rel === "CONTRIBUTING") continue;
    const { data, content } = parseFrontmatter(raw);
    pages.push({
      slug: rel,
      section: sectionOf(rel),
      title: deriveTitle(content, rel),
      body: content,
      frontmatter: data as PlaybookPage["frontmatter"],
      headings: extractHeadings(content),
    });
  }
  pages.sort((a, b) => a.slug.localeCompare(b.slug));
  return pages;
}

export const PLAYBOOK_PAGES: PlaybookPage[] = buildPages();

const BY_SLUG = new Map(PLAYBOOK_PAGES.map((p) => [p.slug, p]));

export function getPlaybookPage(slug: string): PlaybookPage | null {
  // Accept "index", "" and "/" all as the root index.
  if (!slug || slug === "/" || slug === "index") return BY_SLUG.get("index") ?? null;
  return BY_SLUG.get(slug) ?? null;
}

export function listBySection(section: PlaybookPage["section"]): PlaybookPage[] {
  return PLAYBOOK_PAGES.filter((p) => p.section === section);
}

export function recentlyVerifiedPages(limit = 8): PlaybookPage[] {
  return [...PLAYBOOK_PAGES]
    .filter((p) => p.frontmatter.last_verified)
    .sort((a, b) => (b.frontmatter.last_verified ?? "").localeCompare(a.frontmatter.last_verified ?? ""))
    .slice(0, limit);
}

/** Lightweight full-text search index — built once at module load. */
export type SearchHit = {
  slug: string;
  title: string;
  section: PlaybookPage["section"];
  excerpt: string;
  score: number;
};

export function searchPlaybook(query: string, limit = 25): SearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/).filter(Boolean);
  const out: SearchHit[] = [];
  for (const p of PLAYBOOK_PAGES) {
    const hay = (p.title + "\n" + p.body).toLowerCase();
    let score = 0;
    let firstHitAt = -1;
    for (const t of terms) {
      const idx = hay.indexOf(t);
      if (idx >= 0) {
        score += 1;
        if (p.title.toLowerCase().includes(t)) score += 2;
        if (firstHitAt < 0 || idx < firstHitAt) firstHitAt = idx;
      }
    }
    if (score === 0) continue;
    const start = Math.max(0, firstHitAt - 40);
    const excerpt = (firstHitAt >= 0 ? hay.slice(start, start + 180) : p.body.slice(0, 180)).replace(/\s+/g, " ").trim();
    out.push({ slug: p.slug, title: p.title, section: p.section, excerpt, score });
  }
  out.sort((a, b) => b.score - a.score);
  return out.slice(0, limit);
}